import * as React from 'react';
import './app.scss'
import { Redirect, Route } from 'react-router';
import Layout from './Layout';
import Home from '../pages/Home';
import { LoginProfessional, LoginClient } from '../pages/Login';
import LoginRouter from '../pages/Login/LoginRouter';
import LoginContextChooser from '../pages/Login/LoginContextChooser';
import NoClinicsYet from '../pages/Login/NoClinicsYet';
import Profile from '../pages/Profile';
import Clients from '../pages/Clients';
import Professionals from '../pages/Professionals';
import Settings from '../pages/Settings';
import Billing from '../pages/Billing';
import CashRegister from '../pages/CashRegister';
import StockPage from '../pages/Stock';
import RestrictedRoute from '../components/RestrictedRoute';
import { RequireAdministrator, RequireLogout, RequireLogin, RequireProfessionalsManagement, RequirePricesManagement, RequireCampaignManagement, RequireAnalyticsPermission, RequireAppointmentsManagement } from '../components/RequirePermission';
import EntityEditionModal from './EntityEditionModal';
import { paths } from './constants';
import { ActionResult } from '../pages/ActionResult';
import { CampaignsPage } from '../pages/Campaigns';
import { CampaignBuilderPage } from '../pages/Campaigns/CampaignBuilderPage';
import { CampaignAnalyticsPage } from '../pages/Campaigns/Analytics/CampaignAnalyticsPage';
import { MyPacksPage } from '../pages/MyPacks';
import Analytics from '../pages/Analytics';
import Account from '../pages/Account';
import WaitingLists from '../pages/WaitingLists';
import TasksPage from '../pages/Tasks';
import { getLanguage, i18nReady, useTranslations } from '../store/translations/hooks';
import { useState } from 'react';
import moment from 'moment';
import { AnonymousSchedulePage } from '../pages/Home/HomeAnonymous/AnonymousSchedulePage';
import ConsentApproval from '../pages/ConsentApproval';
import ConfirmEmailAction from '../pages/ConfirmEmailAction';
import ResetPasswordAction from '../pages/ResetPasswordAction';
import SurveyResponse from '../pages/SurveyResponse';
import SurveyPreview from '../pages/SurveyPreview';
import CookieBanner from '../components/CookieBanner';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../store';
import { isPaymentExpired, isAdministrator } from '../store/session/selectors';
import { logout } from '../store/session/actions';
import { TK } from '../store/translations/translationKeys';
import styled from 'styled-components';
import AppButton from '../components/AppButton';

import 'moment/locale/pt'
import 'moment/locale/fr'

moment.updateLocale('pt', { months : ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'] });

const ExpiredContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacer-L);
    text-align: center;
    min-height: 60vh;
    gap: var(--spacer-M);
`;

const ExpiredNonAdminScreen: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch();
    return (
        <ExpiredContainer>
            <h3>{t(TK.subscriptionExpired)}</h3>
            <p style={{color: 'var(--text-muted)'}}>{t(TK.subscriptionExpiredContactAdmin)}</p>
            <AppButton color="secondary" onClick={() => dispatch(logout())}>{t(TK.logout)}</AppButton>
        </ExpiredContainer>
    );
};

export interface AppProps {
    onLoad: () => void,
}


export default (props: AppProps) => {
    const [isReady, setIsReady] = useState(i18nReady())

    const userIsLoggedIn = useSelector((state: ApplicationState) => !!state.session.user);
    const paymentExpired = useSelector(isPaymentExpired);
    const userIsAdmin = useSelector(isAdministrator);

    // eslint-disable-next-line
    React.useEffect(() => {
        props.onLoad();

        const urlLocale = location.pathname.split('/', 2)[1];
        moment.locale(urlLocale === 'pt' || urlLocale === 'en' || urlLocale === 'fr' ? urlLocale : getLanguage());

        if (!i18nReady()){
            console.log('App not ready, waiting i18n script')
            const i18nScript = document.getElementById("i18nPlaceholder")
            if (i18nScript) {
                i18nScript.addEventListener('load', () => {
                    console.log('i18n script load event')
                    setIsReady(true)
                })
            } else {
                console.error('App not ready, i18n script not found')
            }
        } else {
            setIsReady(true)
        }
    }, []);

    if (!isReady) return <></>

    if (userIsLoggedIn && paymentExpired) {
        return (
            <Layout>
                <CookieBanner />
                {userIsAdmin ? <Account /> : <ExpiredNonAdminScreen />}
            </Layout>
        );
    }

    return (
        <Layout>
            <CookieBanner />
            <EntityEditionModal />
            <Route exact path="/:lang(en|pt|fr)" render={({ match }) => <Redirect to={`/${match.params.lang}/`} />} />
            <Route path={paths.actionResult()} component={ActionResult} />
            <Route path={paths.anonymousSchedule()} component={AnonymousSchedulePage} />
            <Route exact path={paths.clientConsent()} component={ConsentApproval} />
            <Route exact path={paths.clientSurvey()} component={SurveyResponse} />
            <Route exact path={paths.clientSurveyPreview()} component={SurveyPreview} />
            <Route exact path={paths.confirmEmailAction()} component={ConfirmEmailAction} />
            <Route exact path={paths.resetPasswordAction()} component={ResetPasswordAction} />
            <RestrictedRoute exact path={paths.root()} component={Home} restrictor={RequireLogin} />
            {/* Login routes with smart routing */}
            <RestrictedRoute exact path={`${paths.login()}/chooser`} component={LoginContextChooser} restrictor={RequireLogout} notPermitted={(<Redirect to={paths.root()}/>)} />
            <RestrictedRoute exact path={paths.loginProfessionalOnboarding()} component={LoginProfessional} restrictor={RequireLogout} notPermitted={(<Redirect to={paths.root()}/>)} />
            <RestrictedRoute exact path={paths.loginProfessional()} component={LoginProfessional} restrictor={RequireLogout} notPermitted={(<Redirect to={paths.root()}/>)} />
            <RestrictedRoute exact path={paths.loginClient()} component={LoginClient} restrictor={RequireLogout} notPermitted={(<Redirect to={paths.root()}/>)} />
            <RestrictedRoute exact path={paths.login()} component={LoginRouter} restrictor={RequireLogout} notPermitted={(<Redirect to={paths.root()}/>)} />
            {/* No clinics page for clients without businesses */}
            <RestrictedRoute exact path={paths.noClinics()} component={NoClinicsYet} restrictor={RequireLogin} />
            <RestrictedRoute exact path={paths.account()} component={Account} restrictor={RequireAdministrator} />
            <RestrictedRoute exact path={paths.users()} component={() => <Redirect to={`${paths.account()}?tab=users`} />} restrictor={RequireAdministrator} />
            <RestrictedRoute exact path={paths.mySubscription()} component={() => <Redirect to={`${paths.account()}?tab=subscription`} />} restrictor={RequireAdministrator} />
            <RestrictedRoute exact path={paths.profile()} component={Profile} restrictor={RequireLogin} />
            <RestrictedRoute exact path={paths.clients()} component={Clients} restrictor={RequireLogin} />
            <RestrictedRoute exact path={paths.tasks()} component={TasksPage} restrictor={RequireLogin} />
            <RestrictedRoute exact path={paths.professionals()} component={Professionals} restrictor={RequireProfessionalsManagement} />
            <RestrictedRoute exact path={paths.settings()} component={Settings} restrictor={RequireAdministrator} />
            <RestrictedRoute exact path={`${paths.settings()}/:settingId`} component={Settings} restrictor={RequireAdministrator} />
            <RestrictedRoute exact path={paths.administratorDashboard()} component={() => <Redirect to={`${paths.account()}?tab=admin`} />} restrictor={RequireAdministrator} />
            <RestrictedRoute exact path={paths.analytics()} component={Analytics} restrictor={RequireAnalyticsPermission} />
            <Route exact path={paths.stock()} component={StockPage} />
            <RestrictedRoute exact path={paths.billing()} component={Billing} restrictor={RequireAppointmentsManagement} />
            <Route exact path={paths.cashRegister()} component={CashRegister} />
            <RestrictedRoute exact path={paths.campaignAnalytics(':id')} component={CampaignAnalyticsPage} restrictor={RequireCampaignManagement} />
            <RestrictedRoute exact path={paths.campaign(':id')} component={CampaignBuilderPage} restrictor={RequireCampaignManagement} />
            <RestrictedRoute exact path={paths.campaigns()} component={CampaignsPage} restrictor={RequireCampaignManagement} />
            <RestrictedRoute exact path={paths.myPacks()} component={MyPacksPage} restrictor={RequireLogin} />
            <RestrictedRoute exact path={paths.waitingLists()} component={WaitingLists} restrictor={RequireLogin} />
        </Layout>
    );
}
